<template>
    <div class="onboarding-page">
        <Breadcrumbs :items="breadcrumbs"></Breadcrumbs>
        <div class="content-wrapper">
            <div class="content">
                <section class="wizard" aria-label="Мастер подключения проекта">
                    <div class="wizard-head">
                        <div class="head-copy">
                            <nav class="crumbs" aria-label="Навигация">
                                <div class="crumbs-entry">
                                    <div class="crumbs-item">
                                        <a
                                            href="/projects"
                                            class="crumbs-link"
                                            @click.prevent="goToProjects"
                                        >
                                            Ваши проекты
                                        </a>
                                    </div>
                                </div>
                                <div class="crumbs-entry">
                                    <span class="crumbs-back" aria-hidden="true">←</span>
                                    <div class="crumbs-item">
                                        <span class="crumbs-current">Подключение нового проекта</span>
                                    </div>
                                </div>
                            </nav>
                            <h1>{{ screenTitle }}</h1>
                            <p v-if="screenSubtitle">{{ screenSubtitle }}</p>
                        </div>
                        <div class="progress">
                            <div class="dots" aria-hidden="true">
                                <div
                                    v-for="(s, idx) in markerSteps"
                                    :key="`${s}-${idx}`"
                                    class="dot"
                                    :class="{
                                        active: markerActiveIndex === idx,
                                        done: markerActiveIndex > idx,
                                    }"
                                ></div>
                            </div>
                            <div class="progress-meta">
                                <span>Прогресс</span>
                                <span>{{ progressText }}</span>
                            </div>
                            <div class="progress-track">
                                <div class="progress-fill" :style="{ width: progressFillPercent }"></div>
                            </div>
                        </div>
                    </div>

                    <div class="wizard-body">
                        <div v-if="comment" class="pending-comment-wrap">
                            <h2>Комментарий от разработчика:</h2>
                            <p class="pending-comment">{{ comment }}</p>
                        </div>

                        <!-- Non-marketplace: создание проекта до появления projectId -->
                        <div v-if="altegioMigrationError" role="alert">
                            <p>{{ altegioMigrationError }}</p>
                            <v-btn color="mainNew" :loading="altegioMigrationLoading" @click="prepareAltegioReconnect">Повторить</v-btn>
                        </div>
                        <CreateProjectStep
                            v-else-if="isCreateProjectFlow"
                            :redirect-after-onboarding-path="redirectAfterOnboardingPath"
                            :hide-submit-button="true"
                        />

                        <!-- YC/Altegio marketplace: внутри шага "подключение бота" — подшаги выбора/подключения канала -->
                        <template v-else-if="stepIndex != null && stepIndex === 4 && isYcLikeMarketplace">
                            <ChannelSelectionStep
                                v-if="ycStep === 'channel-selection'"
                                use-external-footer
                                @confirm="onYcChannelSelected"
                            />

                            <template v-else-if="ycStep === 'channel-connection'">
                                <WhatsappChannelConnectionViewOnboarding
                                    v-if="ycSelectedChannelType === 'whatsappChannel'"
                                    :channelType="ycSelectedChannelType"
                                    :redirectOnConnect="false"
                                    :showBack="false"
                                    :embedded="true"
                                    :providerTypeOverride="ycSelectedProviderType"
                                    @back="onYcBackFromConnection"
                                    @connector-created="onYcTempConnectorCreated"
                                    @channel-connected="onYcFirstChannelConnected"
                                />
                                <MaxChannelConnectionViewOnboarding
                                    v-else-if="ycSelectedChannelType === 'maxChannel'"
                                    :channelType="ycSelectedChannelType"
                                    :redirectOnConnect="false"
                                    :showBack="false"
                                    :embedded="true"
                                    :providerTypeOverride="ycSelectedProviderType"
                                    @back="onYcBackFromConnection"
                                    @connector-created="onYcTempConnectorCreated"
                                    @channel-connected="onYcFirstChannelConnected"
                                />
                                <TelegramChannelConnectionViewOnboarding
                                    v-else-if="ycSelectedChannelType === 'telegramChannel'"
                                    :channelType="ycSelectedChannelType"
                                    :redirectOnConnect="false"
                                    :showBack="false"
                                    :embedded="true"
                                    :providerTypeOverride="ycSelectedProviderType"
                                    @back="onYcBackFromConnection"
                                    @connector-created="onYcTempConnectorCreated"
                                    @channel-connected="onYcFirstChannelConnected"
                                />
                            </template>

                            <NotificationSettings
                                v-else
                                ref="ycNotificationsRef"
                                :connector="ycWhatsappBasisConnector"
                                :onboardingMode="true"
                                :initialDefaultChannelId="ycConnectedChannelId"
                                :hideSaveButton="true"
                                @saved="onYcNotificationsSaved"
                                @save-state="onYcNotificationSaveState"
                            />
                        </template>

                        <component
                            v-else-if="stepIndex != null"
                            :is="steps[stepIndex].component"
                            :crmConnectorType="crmConnectorType"
                            @refreshStep="refreshStep"
                            @timezone-changed="onOtherCrmTimezoneChanged"
                        ></component>
                    </div>

                    <div v-if="!altegioMigrationError" class="actions">
                        <div class="left-actions">
                            <button
                                v-if="wizardNav.canBack"
                                class="btn ghost"
                                type="button"
                                @click="onWizardBack"
                            >
                                <v-icon size="18" class="btn-icon">mdi-arrow-left</v-icon>
                                Назад
                            </button>
                        </div>
                        <div class="right-actions">
                            <v-btn
                                v-if="wizardNav.showNext !== false"
                                class="btn-next white--text"
                                color="mainNew"
                                type="button"
                                depressed
                                :disabled="!wizardNav.canNext || wizardNav.nextLoading"
                                :loading="wizardNav.nextLoading"
                                @click="onWizardNext"
                            >
                                {{ wizardNav.nextLabel }}
                            </v-btn>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, getCurrentInstance, ref, reactive, provide, onBeforeMount, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import useRouter from '@/hooks/useRouter'
import { useProject } from '@root/src/hooks/useProject'
import { useAdminStore } from '../../../store/stores/admin'
import api from '../../../api/api'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import  SelectCRMSystemView from '@/components/project/onBoarding/SelectCRMSystemView.vue'
import  AuthorizationInCRMSystemView from '@/components/project/onBoarding/AuthorizationInCRMSystemView.vue'
import  AuthorizationPending from '@/components/project/onBoarding/AuthorizationPending.vue'
import  BotSettingsView from '@/components/project/onBoarding/BotSettingsView.vue'
import  ConnectorSettings from '@/components/project/onBoarding/ConnectorSettings.vue'
import  Billing from '@/modules/marketplace/views/Billing.vue'
import ChannelSelectionStep from '@/components/project/onBoarding/ChannelSelectionStep.vue'
import CreateProjectStep from '@/components/project/onBoarding/CreateProjectStep.vue'
import WhatsappChannelConnectionViewOnboarding from '@/components/project/onBoarding/WhatsappChannelConnectionViewOnboarding.vue'
import MaxChannelConnectionViewOnboarding from '@/components/project/onBoarding/MaxChannelConnectionViewOnboarding.vue'
import TelegramChannelConnectionViewOnboarding from '@/components/project/onBoarding/TelegramChannelConnectionViewOnboarding.vue'
import NotificationSettings from '@/components/ClientNotifications/NotificationSettings.vue'
import { getCredential } from '../../../api/admin'
import store from '../../../store'
import { CONNECTOR_TYPES, CHANNEL_TYPES, PENDING_CONNECTOR_TYPES, PROVIDER_TYPES } from '../../../vars/general'
import { getOnboardingConnectionType, refreshProjectAndAssignChannelSlots } from '@/utils/onboardingHelpers'
import { getCascadesByProject, updateCascadeChannelPriority, ensureAltegioOnboardingCascades } from '@/api/networkFixer'
import { isAltegioMarketplaceReconnect, prepareAltegioMarketplaceReconnect } from '@/helpers/altegioMarketplaceMigration'
import {
    clearPromoRedirectAfterOnboarding,
    isPromoRedirectPath,
    setSuppressPublicMarketplaceRedirect,
    tryRedirectAfterOnboarding,
} from '@/helpers/redirectAfterOnboarding'

export default {
    name: 'OnBoarding',
    components: {
        SelectCRMSystemView,
        AuthorizationInCRMSystemView,
        AuthorizationPending,
        ConnectorSettings,
        BotSettingsView,
        ChannelSelectionStep,
        CreateProjectStep,
        WhatsappChannelConnectionViewOnboarding,
        MaxChannelConnectionViewOnboarding,
        TelegramChannelConnectionViewOnboarding,
        NotificationSettings,
        Breadcrumbs
    },
    props: {

    },

    setup(props) {
        const router = useRouter()
        const root = getCurrentInstance().proxy
        const { project } = useProject()
        const isCreateProjectFlow = computed(() => !router.route.value?.params?.id)
        const redirectAfterOnboardingPath = computed(() => {
            const q = router.route.value?.query?.redirectAfterOnboarding
            return typeof q === 'string' && q.startsWith('/') ? q : null
        })
        const isPromoCreateProjectFlow = computed(() =>
            isPromoRedirectPath(redirectAfterOnboardingPath.value),
        )
        const isYcMarketplace = computed(() => getOnboardingConnectionType(project.value) === 'yc-marketplace')
        const isAltegioMarketplace = computed(
            () => getOnboardingConnectionType(project.value) === 'ag-marketplace',
        )
        // Altegio MP: CRM уже из маркетплейса — тот же сценарий шагов, что у YC MP.
        const isYcLikeMarketplace = computed(() => isYcMarketplace.value || isAltegioMarketplace.value)
        const isAltegioReconnect = computed(() => isAltegioMarketplaceReconnect(project.value))
        const altegioMigrationError = ref('')
        const altegioMigrationLoading = ref(false)
        const ensureAltegioCascades = async (projectId) => {
            const { error } = await ensureAltegioOnboardingCascades(projectId)
            if (error) throw new Error(error.message || 'Не удалось подготовить каскады Altegio')
        }
        const prepareAltegioReconnect = async () => {
            if (altegioMigrationLoading.value) return
            altegioMigrationLoading.value = true
            altegioMigrationError.value = ''
            try {
                const projectId = String(project.value.id)
                const result = await prepareAltegioMarketplaceReconnect(project.value, {
                    ensureBotConnector: async () => {
                        await root.$store.dispatch('project/fetchProject', projectId)
                        const current = root.$store.getters.projectById(projectId)
                        if (!current.connectors?.some(c => c.connectorType === CONNECTOR_TYPES.whatsappBasis && !c.deleted)) {
                            const connector = await root.$store.dispatch('project/createConnector', {
                                projectId, connectorType: CONNECTOR_TYPES.whatsappBasis,
                            })
                            const added = await root.$store.dispatch('project/addConnector', connector)
                            if (added?.error) throw new Error('Не удалось подготовить приложение уведомлений')
                            await root.$store.dispatch('project/fetchProject', projectId)
                        }
                        root.$store.commit('project/updateCurrentAddingConnector', CONNECTOR_TYPES.whatsappBasis)
                    },
                    ensureCascades: () => ensureAltegioCascades(projectId),
                })
                if (!result) return
                if (result.step === 'complete') {
                    await goToProjectPage()
                    return
                }
                stepIndex.value = 4
                ycStep.value = result.step
                ycConnectedChannelId.value = result.channelId
                ycSelectedChannelType.value = null
                ycSelectedProviderType.value = null
            } catch (error) {
                altegioMigrationError.value = error instanceof Error
                    ? error.message
                    : 'Не удалось продолжить подключение Altegio'
            } finally {
                altegioMigrationLoading.value = false
            }
        }
        const isDikidiMarketplace = computed(() => getOnboardingConnectionType(project.value) === 'dd-marketplace')
        const isOtherCrmFlow = computed(
            () =>
                isCreateProjectFlow.value ||
                (!isYcLikeMarketplace.value && !isDikidiMarketplace.value),
        )
        // Other CRM: выбор пояса внутри SelectCRMSystemView (до выбора CRM)
        // Only sessionStorage means the user already confirmed timezone in this flow
        const otherCrmTimezoneSet = ref(Boolean(sessionStorage.getItem('projectTimeZone')))
        const onOtherCrmTimezoneChanged = (timezone) => {
            if (timezone === null) {
                otherCrmTimezoneSet.value = false
                return
            }
            otherCrmTimezoneSet.value = Boolean(timezone || sessionStorage.getItem('projectTimeZone'))
        }

        // Other CRM («Другое»): экран «Готово» после инструкции
        const otherCrmOnFinalScreen = ref(false)
        provide('onboardingOtherCrmFinal', {
            set(value) {
                otherCrmOnFinalScreen.value = Boolean(value)
            },
        })

        // Футер «Назад / Далее» — шаги регистрируют обработчики через provide/inject
        const wizardNav = reactive({
            canBack: false,
            canNext: false,
            nextLoading: false,
            nextLabel: 'Далее',
            showNext: true,
            onNext: null,
            onBack: null,
        })
        const resetWizardNav = () => {
            wizardNav.canBack = false
            wizardNav.canNext = false
            wizardNav.nextLoading = false
            wizardNav.nextLabel = 'Далее'
            wizardNav.showNext = true
            wizardNav.onNext = null
            wizardNav.onBack = null
        }
        provide('onboardingWizardNav', {
            set(partial = {}) {
                Object.assign(wizardNav, partial)
            },
            reset: resetWizardNav,
        })
        const onWizardNext = async () => {
            if (!wizardNav.canNext || wizardNav.nextLoading || typeof wizardNav.onNext !== 'function') {
                return
            }
            await wizardNav.onNext()
        }
        const onWizardBack = async () => {
            if (!wizardNav.canBack || typeof wizardNav.onBack !== 'function') {
                return
            }
            await wizardNav.onBack()
        }

        // YC marketplace: внутренние подшаги выбора/подключения канала
        const ycStep = ref('channel-selection') // 'channel-selection' | 'channel-connection' | 'notifications'
        const ycSelectedChannelType = ref(null)
        const ycSelectedProviderType = ref(null)
        const ycConnectedChannelId = ref(null)
        const ycNotificationsRef = ref(null)
        const ycNotificationSaveState = ref({ canSave: false, saveLoading: false })
        // Если пользователь вернётся назад и выберет другой канал — удаляем ранее созданный (черновик) коннектор канала
        const ycTempCreatedChannelConnectorId = ref(null)
        const ycTempCreatedChannelType = ref(null)
        const ycWhatsappBasisConnector = computed(() =>
            project.value?.connectors?.find((c) => c.connectorType === CONNECTOR_TYPES.whatsappBasis && (!isAltegioReconnect.value || !c.deleted)) || null,
        )

        const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

        /** Ретраи для нестабильных запросов онбординга (createConnector / каскады). */
        const withRetries = async (fn, { attempts = 3, delayMs = 1500, label = 'request' } = {}) => {
            let lastError = null
            for (let attempt = 1; attempt <= attempts; attempt++) {
                try {
                    return await fn(attempt)
                } catch (e) {
                    lastError = e
                    console.warn(`[OnBoarding] ${label}: attempt ${attempt}/${attempts} failed`, e)
                    if (attempt < attempts) {
                        await sleep(delayMs)
                    }
                }
            }
            throw lastError
        }

        const goToProjectPage = async (query = '') => {
            const projectId = project.value?.id
            if (!projectId) return

            await refreshProjectAndAssignChannelSlots({
                store: root.$store,
                projectId,
            })
            router.router.push(`/project/${projectId}${query}`)
        }

        const onYcTempConnectorCreated = (connectorId) => {
            ycTempCreatedChannelConnectorId.value = connectorId
            ycTempCreatedChannelType.value = ycSelectedChannelType.value
        }

        const resolveYcProviderType = (channelType) => {
            if (channelType === CHANNEL_TYPES.whatsappChannel) return PROVIDER_TYPES.whatsappDh
            if (channelType === CHANNEL_TYPES.maxChannel) return PROVIDER_TYPES.maxBasis
            if (channelType === CHANNEL_TYPES.telegramChannel) return PROVIDER_TYPES.telegramDh
            return null
        }

        const onYcChannelSelected = async (channelType) => {
            ycSelectedChannelType.value = channelType
            ycSelectedProviderType.value = resolveYcProviderType(channelType)
            ycStep.value = 'channel-connection'
        }

        const onYcBackToSelection = () => {
            ycSelectedChannelType.value = null
            ycSelectedProviderType.value = null
            ycStep.value = 'channel-selection'
        }

        const onYcBackFromConnection = async () => {
            // Удаляем draft-коннектор канала (раньше это делала кнопка «Назад» внутри экрана)
            const connectorId =
                ycTempCreatedChannelConnectorId.value ||
                project.value?.connectors?.find(
                    (c) =>
                        c &&
                        c.isDraft === true &&
                        c.connectorType === ycSelectedChannelType.value &&
                        (!ycSelectedProviderType.value || c.providerType === ycSelectedProviderType.value),
                )?.connectorId

            if (connectorId) {
                try {
                    await root.$store.dispatch('project/deleteConnector', {
                        connectorId,
                        skipLoading: true,
                    })
                } catch (e) {
                    // ignore
                }
            }
            ycTempCreatedChannelConnectorId.value = null
            ycTempCreatedChannelType.value = null
            // Назад всегда ведём на выбор канала (провайдер выбирается автоматически)
            onYcBackToSelection()
        }
        const onYcNotificationSaveState = (state) => {
            ycNotificationSaveState.value = {
                canSave: Boolean(state?.canSave),
                saveLoading: Boolean(state?.saveLoading),
            }
            if (ycStep.value === 'notifications') {
                wizardNav.canNext = ycNotificationSaveState.value.canSave
                wizardNav.nextLoading = ycNotificationSaveState.value.saveLoading
            }
        }

        // Футер визарда для YC подшагов (выбор канала — через ChannelSelectionStep)
        watch(
            ycStep,
            async (step) => {
                if (!isYcLikeMarketplace.value) return
                await nextTick()
                if (step === 'channel-connection') {
                    Object.assign(wizardNav, {
                        canBack: true,
                        canNext: false,
                        showNext: false,
                        nextLoading: false,
                        nextLabel: 'Далее',
                        onBack: onYcBackFromConnection,
                        onNext: null,
                    })
                    return
                }
                if (step === 'notifications') {
                    Object.assign(wizardNav, {
                        canBack: false,
                        canNext: ycNotificationSaveState.value.canSave,
                        showNext: true,
                        nextLoading: ycNotificationSaveState.value.saveLoading,
                        nextLabel: 'Готово',
                        onBack: null,
                        onNext: async () => {
                            if (typeof ycNotificationsRef.value?.requestSave === 'function') {
                                await ycNotificationsRef.value.requestSave()
                            }
                        },
                    })
                }
            },
            { immediate: true },
        )

        const onYcFirstChannelConnected = async () => {
            const projectId = project.value?.id
            if (!projectId) return

            await root.$store.dispatch('project/fetchProject', projectId)
            const currentProject = root.$store.getters.projectById?.(projectId) || project.value
            const channelConnector = currentProject?.connectors?.find(
                (c) => c && c.connectorType === ycSelectedChannelType.value
            )
            ycConnectedChannelId.value = channelConnector?.channelId || channelConnector?.connectorId || null

            // После подключения канала создаём whatsappBasis (если ещё нет) и переходим к настройкам
            const connectors = currentProject?.connectors || []
            const wb = connectors.find((c) => c.connectorType === CONNECTOR_TYPES.whatsappBasis)
            if (!wb) {
                try {
                    await withRetries(
                        async (attempt) => {
                            // Между ретраями коннектор мог уже появиться
                            if (attempt > 1) {
                                await root.$store.dispatch('project/fetchProject', projectId)
                                const refreshed =
                                    root.$store.getters.projectById?.(projectId) || project.value
                                const existing = (refreshed?.connectors || []).find(
                                    (c) => c && c.connectorType === CONNECTOR_TYPES.whatsappBasis && c.connectorId,
                                )
                                if (existing) return existing
                            }

                            const created = await root.$store.dispatch('project/createConnector', {
                                projectId,
                                connectorType: CONNECTOR_TYPES.whatsappBasis,
                            })
                            const addResult = await root.$store.dispatch('project/addConnector', created)
                            if (addResult?.error) {
                                throw addResult.error instanceof Error
                                    ? addResult.error
                                    : new Error(
                                          addResult.error?.message ||
                                              'Не удалось создать коннектор WhatsApp (whatsappBasis)',
                                      )
                            }
                            await root.$store.dispatch('project/setProjects')
                            await root.$store.dispatch('project/fetchProject', projectId)
                            return created
                        },
                        { attempts: 3, delayMs: 1500, label: 'create whatsappBasis' },
                    )
                } catch (e) {
                    root.$store.dispatch('callNotify', 'Ошибка при создании коннектора WhatsApp (whatsappBasis)')
                    return
                }
            }
            root.$store.commit('project/updateCurrentAddingConnector', CONNECTOR_TYPES.whatsappBasis)
            ycStep.value = 'notifications'
        }

        const onYcNotificationsSaved = () => {
            // После онбординга YC создаём дефолтные каскады (mailings + notifications + crmMessages)
            // и кастомный каскад «Администраторы». Методы идемпотентные, дубликаты не создают.
            ;(async () => {
                try {
                    const projectId = String(project.value.id)
                    if (isAltegioReconnect.value) {
                        await ensureAltegioCascades(projectId)
                        await root.$store.dispatch('project/fetchProject', projectId)
                        await goToProjectPage('?showCascade=true')
                        return
                    }
                    if (isAltegioMarketplace.value) {
                        await withRetries(
                            async () => {
                                const { error: mailingsCascadeError } = await api.networkFixer.cascadeDefaultMailingsCreate(projectId)
                                if (mailingsCascadeError) {
                                    throw new Error(
                                        mailingsCascadeError?.message ||
                                            'Не удалось создать mailings-каскад по умолчанию. Подождите 10-15 секунд и нажмите "Сохранить" еще раз.',
                                    )
                                }
                            },
                            { attempts: 3, delayMs: 1500, label: 'cascadeDefaultNotificationsCreate' },
                        )
                        await withRetries(
                            async () => {
                                const { data, error: notificationsCascadeError } = await api.networkFixer.createCascade({
                                    projectId,
                                    name: `Каскад для сервисных уведомлений`,
                                    cascadeType: 'notifications',
                                    sendType: 'failover',
                                    status: 'active',
                                })
                                if (notificationsCascadeError) {
                                    throw new Error(
                                        notificationsCascadeError?.message ||
                                            'Не удалось создать notifications-каскад по умолчанию. Подождите 10-15 секунд и нажмите "Сохранить" еще раз.',
                                    )
                                }
                            },
                            { attempts: 3, delayMs: 1500, label: 'cascadeDefaultNotificationsCreate' },
                        )
                    } else {
                        await withRetries(
                            async () => {
                                const { error: cascadeError } = await api.networkFixer.cascadeDefaultCreate(projectId)
                                if (cascadeError) {
                                    throw new Error(
                                        cascadeError?.message ||
                                            'Не удалось создать mailings-каскад по умолчанию. Подождите 10-15 секунд и нажмите "Сохранить" еще раз.',
                                    )
                                }
                            },
                            { attempts: 3, delayMs: 1500, label: 'cascadeDefaultCreate' },
                        )
                    }

                    await withRetries(
                        async () => {
                            const { error: administratorsCascadeError } =
                                await api.networkFixer.cascadeDefaultAdministratorsCreate(projectId)
                            if (administratorsCascadeError) {
                                throw new Error(
                                    administratorsCascadeError?.message ||
                                        'Не удалось создать каскад «Администраторы». Подождите 10-15 секунд и нажмите "Сохранить" еще раз.',
                                )
                            }
                        },
                        { attempts: 3, delayMs: 1500, label: 'cascadeDefaultAdministratorsCreate' },
                    )

                    project.value.isOnBoardingActive = false
                    await root.$store.dispatch('project/updateProjectSettings', {
                        projectId: project.value.id,
                        settings: project.value,
                    })

                    // После создания каскадов проставляем приоритет для созданных каналов
                    // (логика аналогична AddChannelSidebarContent.assignLastPriorityToChannel)
                    try {
                        await root.$store.dispatch('project/fetchProject', projectId)
                        const curProject = root.$store.getters.projectById?.(projectId) || project.value
                        const connectors = curProject?.connectors || []

                        const channelConnectors = connectors.filter(
                            (c) =>
                                c &&
                                c.channelId &&
                                [
                                    CHANNEL_TYPES.whatsappChannel,
                                    CHANNEL_TYPES.telegramChannel,
                                    CHANNEL_TYPES.maxChannel,
                                    CHANNEL_TYPES.maxBusiness,
                                ].includes(c.connectorType),
                        )

                        for (const connector of channelConnectors) {
                            const channelId = connector.channelId
                            const providerType = connector.providerType
                            const isWhatsappDh = providerType === PROVIDER_TYPES.whatsapp

                            const { data: cascades, error } = await getCascadesByProject(projectId)
                            if (error || !cascades || cascades.length === 0) continue

                            const cascadeTypes = ['mailings', 'notifications', 'crmMessages']
                            for (const cascadeType of cascadeTypes) {
                                if (cascadeType === 'notifications' && isWhatsappDh) continue
                                const cascade = cascades?.find((c) => c && c.cascadeType === cascadeType)
                                if (!cascade) continue

                                const existingChannels = cascade.channels || []
                                const existingChannel = existingChannels.find(
                                    (ch) => Number(ch?.channelId) === Number(channelId),
                                )

                                // Если канал уже есть в каскаде с валидным приоритетом, не повышаем его повторно.
                                if (Number(existingChannel?.priority) > 0) {
                                    continue
                                }

                                // Считаем max только по другим каналам, чтобы единственный канал получил приоритет 1.
                                const otherChannels = existingChannels.filter(
                                    (ch) => Number(ch?.channelId) !== Number(channelId),
                                )
                                const maxPriority =
                                    otherChannels.length > 0
                                        ? Math.max(...otherChannels.map((ch) => Number(ch?.priority) || 0))
                                        : 0

                                try {
                                    await updateCascadeChannelPriority({
                                        cascadeId: cascade.id,
                                        channelId,
                                        priority: maxPriority + 1,
                                    })
                                } catch (e) {
                                    // ignore: если канал еще не добавлен в каскад, попробуем дальше
                                }
                            }
                        }

                        try {
                            root.$emit('reload-channel-priorities')
                        } catch (e) {
                            // ignore
                        }
                    } catch (e) {
                        // ignore: приоритеты не должны блокировать переход
                    }
                } catch (e) {
                    root.$store.dispatch(
                        'callNotify',
                        e?.message ||
                            'Не удалось создать каскады по умолчанию. Повторите попытку.'
                    )
                    return
                }
                if (await tryRedirectAfterOnboarding(project.value?.id, router.router)) return
                await goToProjectPage('?showCascade=true')
            })()
        }
        const breadcrumbs = [
            {
                text: 'Ваши проекты',
                to: '/projects',
            },
            {
                text: 'Подключение нового проекта',
                disabled: true,
                to: '',
            },
        ]
        const steps = ref([
            {component: SelectCRMSystemView},
            {component: AuthorizationInCRMSystemView},
            {component: AuthorizationPending},
            {component: ConnectorSettings},
            {component: BotSettingsView},
            {component: Billing},
        ])
        const showPendingStep = ref(true)
        const comment = ref('')
        const crmConnectorType = ref('')

        const hasChannelConnector = computed(() =>
            (project.value?.connectors || []).some(
                (c) =>
                    c &&
                    [
                        CHANNEL_TYPES.whatsappChannel,
                        CHANNEL_TYPES.telegramChannel,
                        CHANNEL_TYPES.maxChannel,
                        CHANNEL_TYPES.maxBusiness,
                    ].includes(c.connectorType),
            ),
        )

        /** Выбранная CRM в other-crm онбординге (localStorage не реактивен — sync вручную). */
        const selectedOnboardingCrmType = ref(null)
        const syncSelectedOnboardingCrmType = () => {
            const fromStore = root.$store.state.project.currentAddingConnector
            if (fromStore != null && fromStore !== '') {
                selectedOnboardingCrmType.value = String(fromStore).replace(/"/g, '')
                return
            }
            try {
                const projectId = project.value?.id
                if (projectId) {
                    const raw = window.localStorage.getItem(`${projectId}-onboardingSelectedCrm`)
                    if (raw) {
                        const parsed = JSON.parse(raw)
                        selectedOnboardingCrmType.value = parsed?.type || null
                        return
                    }
                }
            } catch (e) {
                // ignore
            }
            selectedOnboardingCrmType.value = null
        }
        watch(
            () => root.$store.state.project.currentAddingConnector,
            syncSelectedOnboardingCrmType,
        )

        /** CRM «Другое» (API) — без шагов подключения канала в прогрессе. */
        const isOtherApiCrm = computed(() => selectedOnboardingCrmType.value === 'other')

        /**
         * YC (не МП) / Dikidi на шаге «Подключение CRM»: временно 4 маркера вместо 7.
         * При уходе с шага (назад/далее) снова полный прогресс.
         */
        const compactCrmConnectProgress = computed(() => {
            if (!isOtherCrmFlow.value) return false
            const t = selectedOnboardingCrmType.value
            if (t !== CONNECTOR_TYPES.yClients && t !== CONNECTOR_TYPES.dikidi) return false
            // stepIndex объявлен ниже; computed читается после setup
            const si = Number(stepIndex.value)
            return si === 1 && !otherCrmOnFinalScreen.value
        })

        const markerSteps = computed(() => {
            // Other CRM (не YClients/Dikidi marketplace): 5 базовых шагов макета + 2 маркера канала
            // (кроме CRM «Другое» — каналы не подключаем)
            if (isOtherCrmFlow.value) {
                if (compactCrmConnectProgress.value) {
                    return ['project-create', 'timezone', 'crm', 'crm-connect']
                }
                const base = [
                    'project-create',
                    'timezone',
                    'crm',
                    'crm-connect',
                    'done',
                ]
                if (!isOtherApiCrm.value) {
                    base.push('channel-selection', 'channel-connection')
                }
                return base
            }

            const base = []
            // YC / Altegio marketplace: CRM уже из МП — в прогрессе только настройки/каналы/уведомления
            if (isYcLikeMarketplace.value) {
                return [
                    'crm-settings',
                    'channel-selection',
                    'channel-connection',
                    'notifications',
                ]
            }

            // Dikidi marketplace: CRM уже из МП — первые crm/auth/pending/settings скрываем
            if (isDikidiMarketplace.value) {
                return [
                    'dikidi-timezone',
                    'channel-selection',
                    'channel-connection',
                ]
            }

            // В маркерах всегда показываем pending-стадию, даже если фактически она может быть пропущена.
            // Это нужно, чтобы количество маркеров оставалось стабильным.
            base.push('crm', 'auth', 'pending', 'crm-settings')
            base.push('channel-selection')
            base.push('channel-connection')
            return base
        })

        const markerActiveIndex = computed(() => {
            // Other CRM:
            // project-create(0) -> timezone(1) -> crm(2) -> crm-connect(3: auth)
            // -> done(4: AuthorizationPending / «Другое» финал)
            // -> channel-selection(5) -> channel-connection(6) — не для CRM «Другое»
            if (isOtherCrmFlow.value) {
                if (isCreateProjectFlow.value) {
                    return 0
                }

                const si = Number(stepIndex.value)
                if (si === 0) {
                    return otherCrmTimezoneSet.value ? 2 : 1
                }
                if (si === 1) {
                    // Ввод данных CRM; для «Другое» финальный экран внутри auth = done
                    if (otherCrmOnFinalScreen.value) return 4
                    // YC/Dikidi compact: 4 маркера, активный — crm-connect
                    return 3
                }
                if (si === 2 || si === 3) {
                    // AuthorizationPending / Settings — шаг «Готово»
                    return 4
                }
                if (isOtherApiCrm.value) {
                    return 4
                }
                return hasChannelConnector.value ? 6 : 5
            }

            if (isCreateProjectFlow.value) {
                return 0
            }

            const si = Number(stepIndex.value)
            // Dikidi marketplace: timezone(0) -> channel-selection(1) -> channel-connection(2)
            if (isDikidiMarketplace.value) {
                if (si < 4) {
                    return 0
                }
                const needsTimezone = !project.value?.timezone
                if (needsTimezone) return 0
                return hasChannelConnector.value ? 2 : 1
            }

            // YC marketplace: crm/auth/pending в маркерах нет.
            // markerSteps: crm-settings(0) -> channel-selection(1) -> channel-connection -> notifications
            if (si < 4) {
                return 0
            }

            const subSteps = ['channel-selection', 'channel-connection', 'notifications']
            const subIdx = Math.max(0, subSteps.indexOf(ycStep.value))

            // crm-settings занимает индекс 0, подшаги бота начинаются с 1
            return 1 + subIdx
        })

        const SCREEN_META_BY_MARKER = {
            'project-create': [
                'Создание проекта',
                'Укажите базовые данные проекта перед подключением внешней CRM.',
            ],
            timezone: [
                'Часовой пояс',
                'Выберите часовой пояс проекта для корректного расписания и отчётов.',
            ],
            crm: [
                'Выбор CRM',
                'Выберите CRM-систему, из которой Data Heroes будет получать данные.',
            ],
            'crm-connect': [
                'Подключение CRM',
                'Выполните инструкцию и заполните данные для интеграции.',
            ],
            done: ['Готово', 'Данные получены, скоро мы свяжемся с вами.'],
            auth: ['Авторизация', 'Авторизуйтесь в выбранной CRM-системе.'],
            pending: ['Готово', 'Данные получены, скоро мы свяжемся с вами.'],
            'crm-settings': ['Настройки CRM', 'Заполните настройки подключения к CRM.'],
            'dikidi-timezone': [
                'Часовой пояс',
                'Укажите часовой пояс проекта перед подключением канала.',
            ],
            'channel-selection': [
                'Выберите канал',
                'Выберите канал для отправки уведомлений клиентам.',
            ],
            'channel-connection': [
                'Подключение канала',
                'Выполните подключение выбранного канала.',
            ],
            notifications: [
                'Настройки уведомлений',
                'Настройте сервисные уведомления и режим работы.',
            ],
        }

        const screenMeta = computed(() => {
            const key = markerSteps.value[markerActiveIndex.value]
            // «Другое»: на финальном экране внутри auth заголовок «Закрыть»
            if (key === 'done' && otherCrmOnFinalScreen.value) {
                return ['Закрыть', '']
            }
            if (key === 'project-create' && isPromoCreateProjectFlow.value) {
                return [
                    'Создание проекта',
                    'Чтобы воспользоваться промопредложением, вам нужно создать проект',
                ]
            }
            return SCREEN_META_BY_MARKER[key] || ['Подключение', 'Пройдите шаги подключения проекта.']
        })
        const screenTitle = computed(() => screenMeta.value[0])
        const screenSubtitle = computed(() => screenMeta.value[1])
        const progressText = computed(() => {
            const total = markerSteps.value.length || 1
            const current = Math.min(markerActiveIndex.value + 1, total)
            return `${current} из ${total}`
        })
        const progressFillPercent = computed(() => {
            const total = markerSteps.value.length || 1
            const current = Math.min(markerActiveIndex.value + 1, total)
            return `${(current / total) * 100}%`
        })

        const normalizeConnectorType = (value) => {
            const type = String(value ?? '').replace(/"/g, '')
            if (!type || type === 'null' || type === 'undefined') return ''
            return type
        }
        const parseOnboardingCrmType = (raw) => {
            if (!raw) return ''
            try {
                const parsed = JSON.parse(raw)
                return normalizeConnectorType(parsed?.type)
            } catch (e) {
                return ''
            }
        }
        const getCurrentStep = async () => {
            if (isCreateProjectFlow.value || !project.value) {
                return null
            }

            const allConnectors = project.value.connectors || []
            const haveConnector = allConnectors.length

            const selectedCrm = window.localStorage.getItem(project.value.id + '-onboardingSelectedCrm')
            if (!haveConnector) {
                const crmType = parseOnboardingCrmType(selectedCrm)
                if (crmType) {
                    const currentType = normalizeConnectorType(store.state.project.currentAddingConnector)
                    if (currentType !== crmType) {
                        store.commit('project/updateCurrentAddingConnector', crmType)
                    }
                    return 1
                }
                if (selectedCrm) {
                    window.localStorage.removeItem(project.value.id + '-onboardingSelectedCrm')
                }
                return 0
            }
            let pendingConnector = null
            try {
                const pendingConnectors = await api.project.getPendingConnectorsByProject({
                    projectId: project.value.id,
                })
                pendingConnector = Array.isArray(pendingConnectors?.data)
                    ? pendingConnectors.data[0]
                    : null
            } catch (e) {
                console.warn('[OnBoarding] getPendingConnectorsByProject failed', e)
            }
            if (pendingConnector) {
                try {
                    comment.value = await getCredential({
                        connectorId: pendingConnector.connectorId,
                        name: 'pendingComment',
                    }).then((res) => res?.data)
                } catch (e) {
                    comment.value = ''
                }
                if (comment.value) {
                    store.commit('project/updateCurrentAddingConnector', pendingConnector.connectorType)
                    localStorage.setItem(project.value.id + '-onboardingSelectedCrm', pendingConnector)
                    return 1
                }
                return 2
            }

            const crmConnector = allConnectors.find(
              (connector) => connector.connectorType !== 'whatsappBasis' && connector.connectorType !== 'whatsapp'
            )

            crmConnectorType.value = crmConnector?.connectorType
            if (crmConnector?.isDraft) {
                // CRM без полей настроек: тот же финальный экран, что и AuthorizationPending
                const waitManagerTypes = [
                    CONNECTOR_TYPES.yClients,
                    CONNECTOR_TYPES.profSalon,
                    CONNECTOR_TYPES.profSalonBat,
                    CONNECTOR_TYPES.profSalonBatV2,
                    CONNECTOR_TYPES.altegio,
                ]
                if (waitManagerTypes.includes(crmConnector.connectorType)) {
                    return 2
                } else {
                    return 3
                }
            }

            const basisConnector = allConnectors.find(
                (connector) => connector.connectorType === 'whatsappBasis' || connector.connectorType === 'whatsapp'
            )

            const isNeedToGoOnBot =
                (!basisConnector || basisConnector?.isDraft || !basisConnector?.alive ) &&
                allConnectors.filter((a) => a.connectorId).length > 0

            if (isNeedToGoOnBot) {
                return 4
            }

            // Флаг ещё включён — не уходим на страницу проекта, иначе App вернёт сюда и получится цикл.
            if (project.value?.isOnBoardingActive) {
                return 4
            }

            if (await tryRedirectAfterOnboarding(project.value?.id, router.router)) return
            await goToProjectPage()
        }
        const stepIndex = ref()
        const isShowPendingStep = () => {
            if (isCreateProjectFlow.value || !project.value?.id) {
                return true
            }
            let selectedCrm = null
            try {
                selectedCrm = JSON.parse(
                    window.localStorage.getItem(project.value.id + '-onboardingSelectedCrm'),
                )
            } catch (e) {
                selectedCrm = null
            }
            if (!selectedCrm) {
                return true
            } else {
                return PENDING_CONNECTOR_TYPES.includes(selectedCrm.type)
            }
        }
        const stepBack = () => {
            this.stepIndex--
        }
        const refreshStep = async () => {
            syncSelectedOnboardingCrmType()
            if (isCreateProjectFlow.value) {
                stepIndex.value = null
                return
            }
            if (sessionStorage.getItem('projectTimeZone')) {
                otherCrmTimezoneSet.value = true
            }
            try {
                stepIndex.value = await getCurrentStep()
            } catch (e) {
                console.warn('[OnBoarding] getCurrentStep failed', e)
                if (stepIndex.value == null && isYcLikeMarketplace.value) {
                    stepIndex.value = 4
                }
            }
            try {
                showPendingStep.value = isShowPendingStep()
            } catch (e) {
                showPendingStep.value = true
            }
            // При возврате/перезаходе на шаг 4 восстанавливаем YC/AG подшаги по состоянию проекта,
            // чтобы при перезагрузке не улетать на выбор канала.
            if (stepIndex.value === 4 && isYcLikeMarketplace.value) {
                const projectId = project.value?.id
                try {
                    if (projectId) {
                        await root.$store.dispatch('project/fetchProject', projectId)
                    }
                } catch (e) {
                    // ignore
                }
                const currentProject = projectId ? (root.$store.getters.projectById?.(projectId) || project.value) : project.value
                const connectors = currentProject?.connectors || []
                const channelConnectors = connectors.filter(
                    (c) =>
                        c &&
                        c.channelId &&
                        Object.values(CHANNEL_TYPES).includes(c.connectorType),
                )

                const wb = connectors.find((c) => c && c.connectorType === CONNECTOR_TYPES.whatsappBasis)
                if (wb && channelConnectors.length === 0) {
                    ycStep.value = 'channel-connection'
                    ycSelectedChannelType.value = null
                    ycSelectedProviderType.value = null
                    ycConnectedChannelId.value = null
                    ycTempCreatedChannelConnectorId.value = null
                    ycTempCreatedChannelType.value = null
                    return
                } else if(wb && channelConnectors.length > 0) {
                    ycStep.value = 'notifications'
                    // если есть канал — подставим его id, чтобы defaultChannel в настройках мог заполниться
                    const anyChannel = connectors.find((c) =>
                        c &&
                        [
                            CHANNEL_TYPES.whatsappChannel,
                            CHANNEL_TYPES.telegramChannel,
                            CHANNEL_TYPES.maxChannel,
                        ].includes(c.connectorType),
                    )
                    ycConnectedChannelId.value = anyChannel?.channelId || anyChannel?.connectorId || null
                    return
                }

                const draftChannels = connectors.filter(
                    (c) =>
                        c &&
                        c.isDraft === true &&
                        [
                            CHANNEL_TYPES.whatsappChannel,
                            CHANNEL_TYPES.telegramChannel,
                            CHANNEL_TYPES.maxChannel,
                        ].includes(c.connectorType),
                )
                if (draftChannels.length > 0) {
                    const last = draftChannels[draftChannels.length - 1]
                    ycSelectedChannelType.value = last.connectorType
                    ycSelectedProviderType.value = last.providerType || resolveYcProviderType(last.connectorType)
                    ycConnectedChannelId.value = last.channelId || last.connectorId || null
                    ycStep.value = 'channel-connection'
                    return
                }

                // дефолт — первый выбор канала
                ycStep.value = 'channel-selection'
                ycSelectedChannelType.value = null
                ycSelectedProviderType.value = null
                ycConnectedChannelId.value = null
                ycTempCreatedChannelConnectorId.value = null
                ycTempCreatedChannelType.value = null
            }
        }
        onBeforeMount(async () => {
            root.$store.commit('setHideAppTopbar', true)
            setSuppressPublicMarketplaceRedirect(false)

            if (isCreateProjectFlow.value) {
                stepIndex.value = null
                return
            }

            if (isAltegioReconnect.value) {
                await prepareAltegioReconnect()
                return
            }

            try {
                await refreshStep()
            } catch (e) {
                console.warn('[OnBoarding] refreshStep failed', e)
                if (stepIndex.value == null && isYcLikeMarketplace.value) {
                    stepIndex.value = 4
                }
            }

            const projectId = project.value?.id
            if (!projectId) return

            const connectors = project.value?.connectors || []
            const channelConnectors = connectors.filter(
                (c) =>
                    c &&
                    c.channelId &&
                    Object.values(CHANNEL_TYPES).includes(c.connectorType),
            )
            const isOnBoardingActive = computed(() => project.value?.isOnBoardingActive)
            const currentStep = computed(() => ycStep.value)
            try {
                const { data: cascades } = await getCascadesByProject(projectId)
                if (!isOnBoardingActive.value && channelConnectors.length > 0 && cascades && cascades.length > 0) {
                    await goToProjectPage()
                    return
                } else if (!isOnBoardingActive.value && channelConnectors.length > 0) {
                    ycStep.value = 'notifications'
                    ycSelectedChannelType.value = null
                    ycSelectedProviderType.value = null
                    ycConnectedChannelId.value = null
                    ycTempCreatedChannelConnectorId.value = null
                    ycTempCreatedChannelType.value = null
                } else if (!isOnBoardingActive.value && channelConnectors.length === 0) {
                    ycStep.value = 'channel-selection'
                    ycSelectedChannelType.value = null
                    ycSelectedProviderType.value = null
                    ycConnectedChannelId.value = null
                    ycTempCreatedChannelConnectorId.value = null
                    ycTempCreatedChannelType.value = null
                } else if (currentStep.value !== 'notifications') {
                    if (channelConnectors.length > 0) {
                        channelConnectors.forEach(async (c) => {
                            await root.$store.dispatch('project/deleteConnector', c.connectorId)
                        })
                    }
                }
            } catch (e) {
                console.warn('[OnBoarding] getCascadesByProject failed', e)
            }
        })

        // CreateProjectStep → /project/:id/onBoarding: компонент не remount'ится,
        // onBeforeMount уже отработал с stepIndex=null — догоняем шаг после появления id.
        watch(
            () => router.route.value?.params?.id,
            async (projectId, prevId) => {
                if (!projectId || projectId === prevId) return
                if (isCreateProjectFlow.value) return
                otherCrmTimezoneSet.value = Boolean(sessionStorage.getItem('projectTimeZone'))
                try {
                    await refreshStep()
                } catch (e) {
                    console.warn('[OnBoarding] refreshStep after route change failed', e)
                }
            },
        )

        // Скролл вверх при смене шага (первый заход не трогаем — страница и так сверху).
        const suppressOnboardingStepScroll = ref(true)
        const botViewKey = ref('')
        provide('setOnboardingBotViewKey', (key) => {
            botViewKey.value = key || ''
        })

        const scrollOnboardingToTop = () => {
            window.scrollTo(0, 0)
            if (document.documentElement) document.documentElement.scrollTop = 0
            if (document.body) document.body.scrollTop = 0
        }

        const onboardingViewKey = computed(() => {
            if (isCreateProjectFlow.value) return 'create-project'
            if (stepIndex.value == null) return 'loading'

            const si = Number(stepIndex.value)
            if (isYcLikeMarketplace.value && si === 4) {
                return `yc:${ycStep.value}`
            }

            let intra = ''
            if (isOtherCrmFlow.value) {
                if (si === 0) intra = otherCrmTimezoneSet.value ? ':crm' : ':tz'
                else if (si === 1) intra = otherCrmOnFinalScreen.value ? ':final' : ':auth'
            }

            if (si === 4) {
                return `step:4${intra}|bot:${botViewKey.value || 'init'}`
            }

            return `step:${si}${intra}`
        })

        watch(onboardingViewKey, (key, prevKey) => {
            if (suppressOnboardingStepScroll.value) return
            if (!key || key === 'loading' || key === prevKey) return
            nextTick(scrollOnboardingToTop)
        })

        onMounted(() => {
            nextTick(() => {
                suppressOnboardingStepScroll.value = false
            })
        })

        const goToProjects = async () => {
            try {
                await root.$store.dispatch('project/setProjects')
            } catch (e) {
                // список обновим по возможности; навигацию не блокируем
            }
            router.router.push({ name: 'Projects' })
        }

        onBeforeUnmount(() => {
            root.$store.commit('setHideAppTopbar', false)
        })

        return {
            breadcrumbs,
            steps,
            stepIndex,
            stepBack,
            refreshStep,
            getCurrentStep,
            comment,
            showPendingStep,
            crmConnectorType,
            markerSteps,
            markerActiveIndex,
            screenTitle,
            screenSubtitle,
            progressText,
            progressFillPercent,
            wizardNav,
            onWizardNext,
            onWizardBack,
            isCreateProjectFlow,
            redirectAfterOnboardingPath,
            isYcMarketplace,
            isAltegioMarketplace,
            altegioMigrationError,
            altegioMigrationLoading,
            prepareAltegioReconnect,
            isYcLikeMarketplace,
            onOtherCrmTimezoneChanged,
            ycStep,
            ycSelectedChannelType,
            ycSelectedProviderType,
            ycConnectedChannelId,
            ycNotificationsRef,
            ycWhatsappBasisConnector,
            ycTempCreatedChannelConnectorId,
            ycTempCreatedChannelType,
            onYcChannelSelected,
            onYcBackToSelection,
            onYcBackFromConnection,
            onYcFirstChannelConnected,
            onYcNotificationsSaved,
            onYcNotificationSaveState,
            onYcTempConnectorCreated,
            goToProjects,
        }
    },
    beforeRouteEnter(to, _from, next) {
        if (to.name === 'OnBoardingCreateProject' && !isPromoRedirectPath(to.query?.redirectAfterOnboarding)) {
            clearPromoRedirectAfterOnboarding()
        }
        next()
    },
    beforeRouteLeave(to, from, next) {
        if (from.name === 'OnBoardingCreateProject' && to.name !== 'SelectCRMSystem') {
            clearPromoRedirectAfterOnboarding()
        }
        next()
    },
}
</script>
<style lang="sass" scoped>
@import '@/vars.sass'

// Нет в vars.sass: акцент прогресса из макета
$ob-primary-2: #3F8EFC

.onboarding-page
    color: $dark-new
    font-family: $font-family-base
    width: 100%
    max-width: 100%
    min-width: 0
    overflow-x: hidden
    box-sizing: border-box

    // Внешние content-wrapper / content — как в App/GCB2 (отступ под шапку и уведомления).
    // Вложенные из шагов онбординга сбрасываем, чтобы не было двойного margin.
    > .content-wrapper
        padding: 0 clamp(12px, 2vw, 24px)
        min-height: calc(100vh - var(--app-topbar-extra-height, 0px) - var(--app-notification-height, 0px))
        box-sizing: border-box
        margin-bottom: 0
        align-items: center
        width: 100%
        max-width: 100%
        min-width: 0
        overflow-x: hidden

    > .content-wrapper > .content
        max-width: min(980px, 100%)
        width: 100%
        min-width: 0
        margin-bottom: 24px
        padding-inline: 0
        box-sizing: border-box

.wizard
    width: 100%
    max-width: 100%
    min-width: 0
    display: grid
    grid-template-rows: auto minmax(0, 1fr) auto
    min-height: 560px
    background: #fff
    border: 1px solid $border-new
    border-radius: 24px
    box-shadow: 0 18px 48px rgba(26, 26, 46, 0.10)
    overflow: hidden
    box-sizing: border-box

.wizard-head
    display: grid
    grid-template-columns: minmax(0, 1fr) auto
    gap: 24px
    align-items: start
    padding: 28px 32px 20px
    border-bottom: 1px solid $border-new
    background: linear-gradient(180deg, #fff, #FBFCFF)
    min-width: 0

.crumbs
    min-width: 0
    max-width: 100%
    display: flex
    align-items: center
    gap: 8px
    margin-bottom: 12px
    color: $muted-new
    font-size: 12px
    font-weight: 700
    line-height: 1
    flex-wrap: wrap

.crumbs-entry
    min-width: 0
    display: inline-flex
    align-items: center
    gap: 8px
    max-width: 100%

.crumbs-item
    min-width: 0
    display: inline-flex
    align-items: center
    gap: 6px
    max-width: 100%

.crumbs-link
    min-width: 0
    max-width: 100%
    color: $muted-new !important
    font-weight: 700
    transition: color 0.15s ease
    text-decoration: none
    cursor: pointer
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

    &:hover
        color: $main-new !important

.crumbs-back
    flex: 0 0 auto
    color: $muted-light-new
    font-size: 13px
    transform: translateY(-1px)

.crumbs-current
    overflow: hidden
    max-width: 100%
    color: $muted-new !important
    font-weight: 700
    text-overflow: ellipsis
    white-space: nowrap

.head-copy
    min-width: 0
    max-width: 100%
    h1
        font-size: 28px
        line-height: 1.05
        letter-spacing: -0.05em
        margin: 0 0 8px
        font-weight: 800
        color: $dark-new
        overflow-wrap: anywhere
    p
        margin: 0
        color: $muted-new
        max-width: 680px
        font-size: 14px
        line-height: 1.45

.progress
    min-width: 0
    width: 268px
    max-width: 100%
    padding-top: 4px
    box-sizing: border-box

.dots
    display: flex
    gap: 8px
    justify-content: flex-end
    margin-bottom: 12px
    flex-wrap: wrap

.dot
    width: 14px
    height: 14px
    border-radius: 50%
    border: 2px solid #D8DEEC
    background: $border-light-new
    box-sizing: border-box
    flex: 0 0 auto
    &.done,
    &.active
        background: $ob-primary-2
        border-color: $ob-primary-2
    &.active
        box-shadow: 0 0 0 5px rgba(63, 142, 252, 0.12)

.progress-meta
    display: flex
    justify-content: space-between
    align-items: baseline
    gap: 12px
    color: $muted-light-new
    font-size: 11px
    font-weight: 800
    text-transform: uppercase
    letter-spacing: 0.08em
    min-width: 0
    span
        min-width: 0
        white-space: nowrap
        &:last-child
            flex: 0 0 auto

.progress-track
    height: 7px
    border-radius: 999px
    background: $border-light-new
    margin-top: 8px
    overflow: hidden
    width: 100%

.progress-fill
    height: 100%
    background: linear-gradient(90deg, $main-new, $ob-primary-2)
    border-radius: 999px
    transition: width 0.25s ease

.wizard-body
    min-height: 0
    min-width: 0
    max-width: 100%
    padding: 32px
    display: flex
    flex-direction: column
    align-items: stretch
    animation: onboarding-fade 0.22s ease both
    box-sizing: border-box
    // Шаги онбординга сами рисуют .content-wrapper/.content — внутри wizard сбрасываем App-отступы
    ::v-deep .content-wrapper
        display: block
        width: 100%
        max-width: 100%
        min-width: 0
        margin: 0 !important
        padding: 0
        flex-grow: 0
        justify-content: stretch
        box-sizing: border-box
        // не ставить overflow-x: hidden — иначе overflow-y тоже клипится и режет тени карточек
        overflow: visible
    ::v-deep .content
        width: 100%
        max-width: 100%
        min-width: 0
        margin: 0 !important
        box-sizing: border-box
        overflow: visible
        > div
            margin-top: 0 !important
            max-width: 100%
            min-width: 0
            box-sizing: border-box
    ::v-deep .connect-panel,
    ::v-deep .channel-selection-step,
    ::v-deep .channel-panel,
    ::v-deep .notification-settings,
    ::v-deep .timezone-stage,
    ::v-deep .timezone-card,
    ::v-deep .crm-auth-connect,
    ::v-deep .connect-grid,
    ::v-deep .panel
        max-width: 100%
        min-width: 0
        box-sizing: border-box

.actions
    display: flex
    align-items: center
    justify-content: space-between
    gap: 16px
    padding: 16px 32px
    border-top: 1px solid $border-new
    background: #FBFCFF

.left-actions,
.right-actions
    display: flex
    align-items: center
    gap: 12px

.right-actions
    margin-left: auto

.btn
    height: 42px
    border: 0
    border-radius: 12px
    padding: 0 18px
    font-size: 13px
    font-weight: 900
    display: inline-flex
    align-items: center
    justify-content: center
    gap: 8px
    white-space: nowrap
    cursor: pointer
    font-family: inherit
    &:disabled
        cursor: not-allowed
        opacity: 0.48

    &.ghost
        background: transparent
        color: #596174

.btn-icon
    color: inherit !important
    margin-right: 2px

.btn-next
    height: 42px !important
    min-width: 120px
    border-radius: 12px !important
    padding: 0 18px !important
    font-size: 13px !important
    font-weight: 900 !important
    letter-spacing: 0 !important
    text-transform: none !important
    box-shadow: 0 10px 20px rgba(82, 90, 213, 0.24) !important
    &:disabled
        box-shadow: none !important

@keyframes onboarding-fade
    from
        opacity: 0
        transform: translateY(8px)
    to
        opacity: 1
        transform: none

::v-deep .titleBox
    width: 100%
    font-size: 24px
    letter-spacing: -0.035em
    text-align: left
    font-weight: 800
    color: $dark-new

.pending-comment-wrap
    width: 100%
    margin: 0 0 20px
    color: $alert-new
    h2
        font-size: 16px
        margin: 0 0 8px
    .pending-comment
        width: 100%
        font-size: 14px
        padding: 14px 16px
        border: 1px solid rgba(232, 24, 60, 0.35)
        border-radius: 14px
        background: #FFF5F7
        margin: 0

::v-deep .notification-settings
    padding-top: 0

::v-deep .waiting-for-settings
    padding: 0 8px

@media (max-width: 900px)
    .wizard
        border-radius: 20px
    .wizard-head
        grid-template-columns: 1fr
        gap: 16px
        padding: 24px
    .progress
        width: 100%
        min-width: 0
        max-width: 100%
    .dots
        justify-content: flex-start
    .wizard-body
        padding: 24px
    ::v-deep .connect-panel
        padding: 20px
        border-radius: 20px

@media (max-width: 640px)
    .onboarding-page > .content-wrapper
        padding: 0 10px
    .wizard
        min-height: calc(100vh - 160px)
        border-radius: 18px
    .wizard-head
        padding: 16px
        gap: 12px
    .head-copy h1
        font-size: 22px
    .head-copy p
        font-size: 13px
    .crumbs
        font-size: 11px
        gap: 6px
    .progress-meta
        font-size: 10px
        letter-spacing: 0.04em
        gap: 8px
    .wizard-body
        padding: 14px
    ::v-deep .connect-panel
        padding: 16px
        border-radius: 16px
        min-height: 0
    ::v-deep .section-title
        flex-wrap: wrap
        gap: 8px
        h2
            font-size: 20px
    .actions
        padding: 14px 16px
        align-items: stretch
        flex-direction: row
        gap: 12px
    .left-actions,
    .right-actions
        flex: 1 1 0
        min-width: 0
        width: auto
    .left-actions .btn,
    .right-actions .btn-next
        width: 100%
        min-width: 0
        flex: 1
</style>
